/**
 * LegalTemplates PDF Editor — true redaction
 *
 * A redaction box used to be paint: a black rectangle drawn over text that
 * stayed in the file. Now, when a document with redactions is saved, every
 * redacted page is flattened: the page (boxes included) is rasterised and the
 * original page — its text, images and form widgets — is replaced by that
 * image. The server then rewrites the file so the orphaned page objects are
 * dropped for good (see PdfOnlineController::finalizeRedactedPdfBytes).
 *
 * The review step (confirmRedactions) tells the person what will happen
 * before anything is written.
 */
import { ensurePdfLib, libs, pdfjsLib, state } from './context.js';

// 200 dpi keeps small print legible in the flattened page while holding a
// letter-size PNG around a few hundred kilobytes.
const REDACT_RENDER_DPI = 200;

export function getRedactedPages() {
    const pages = new Set();
    state.annotations.forEach((annotation) => {
        if (annotation && annotation.type === 'redact') {
            pages.add(Number(annotation.page));
        }
    });
    return Array.from(pages).filter((page) => page > 0).sort((a, b) => a - b);
}

export function hasPendingRedactions() {
    return getRedactedPages().length > 0;
}

function describeRedactions() {
    const pages = getRedactedPages();
    const count = state.annotations.filter((annotation) => annotation && annotation.type === 'redact').length;
    const pageList = pages.length > 1
        ? `pages ${pages.slice(0, -1).join(', ')} and ${pages[pages.length - 1]}`
        : `page ${pages[0]}`;
    return `${count} redaction${count === 1 ? '' : 's'} on ${pageList}.`;
}

/**
 * Review step. Resolves true when the person confirms, false otherwise.
 * Without the modal in the page (older markup) it resolves true.
 */
export function confirmRedactions() {
    const modal = document.getElementById('pdf-redact-modal');
    const summaryEl = document.getElementById('pdf-redact-summary');
    const confirmButton = document.getElementById('pdf-redact-confirm');
    const cancelButton = document.getElementById('pdf-redact-cancel');
    const closeButton = document.getElementById('pdf-redact-close');
    if (!modal || !confirmButton) {
        return Promise.resolve(true);
    }
    if (summaryEl) {
        summaryEl.textContent = describeRedactions();
    }

    return new Promise((resolve) => {
        const finish = (confirmed) => {
            modal.classList.remove('active');
            modal.setAttribute('aria-hidden', 'true');
            confirmButton.removeEventListener('click', onConfirm);
            if (cancelButton) {
                cancelButton.removeEventListener('click', onCancel);
            }
            if (closeButton) {
                closeButton.removeEventListener('click', onCancel);
            }
            modal.removeEventListener('click', onBackdrop);
            document.removeEventListener('keydown', onKeydown, true);
            resolve(confirmed);
        };
        const onConfirm = () => finish(true);
        const onCancel = () => finish(false);
        const onBackdrop = (event) => {
            if (event.target === modal) {
                finish(false);
            }
        };
        const onKeydown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                event.preventDefault();
                finish(false);
            }
        };

        confirmButton.addEventListener('click', onConfirm);
        if (cancelButton) {
            cancelButton.addEventListener('click', onCancel);
        }
        if (closeButton) {
            closeButton.addEventListener('click', onCancel);
        }
        modal.addEventListener('click', onBackdrop);
        document.addEventListener('keydown', onKeydown, true);

        modal.classList.add('active');
        modal.setAttribute('aria-hidden', 'false');
        confirmButton.focus();
    });
}

function canvasToPngBytes(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Could not rasterise the redacted page.'));
                return;
            }
            blob.arrayBuffer().then((buffer) => resolve(new Uint8Array(buffer))).catch(reject);
        }, 'image/png');
    });
}

// Form fields whose widgets sit on a flattened page are removed: their
// appearance is already part of the raster and a widget pointing at a page
// that no longer exists confuses viewers.
function removeFormFieldsOnPage(pdfDoc, pageRef) {
    const PDFLib = libs.PDFLib;
    try {
        if (!pdfDoc.catalog.has(PDFLib.PDFName.of('AcroForm'))) {
            return;
        }
        const form = pdfDoc.getForm();
        form.getFields().forEach((field) => {
            let onPage = false;
            try {
                onPage = field.acroField.getWidgets().some((widget) => {
                    const owner = widget.P();
                    return owner && owner === pageRef;
                });
            } catch (error) {
                onPage = false;
            }
            if (onPage) {
                form.removeField(field);
            }
        });
    } catch (error) {
        // Best effort: a malformed AcroForm must not block the redaction.
    }
}

function scrubDocumentMetadata(pdfDoc) {
    const PDFLib = libs.PDFLib;
    try {
        pdfDoc.catalog.delete(PDFLib.PDFName.of('Metadata'));
    } catch (error) {
        // Ignore.
    }
    try {
        pdfDoc.setModificationDate(new Date());
    } catch (error) {
        // Ignore.
    }
}

/**
 * Replace each listed page of `pdfDoc` with a rasterised copy of itself.
 * Call after every annotation (the black boxes included) has been drawn.
 */
export async function flattenRedactedPages(pdfDoc, pageNumbers) {
    const pages = Array.from(new Set(pageNumbers)).sort((a, b) => a - b);
    if (!pages.length) {
        return;
    }
    await ensurePdfLib();

    // Render from the edited document so the boxes and any other edits on
    // the page are part of the image.
    const editedBytes = await pdfDoc.save();
    const loadingTask = pdfjsLib.getDocument({ data: editedBytes.slice() });
    const renderDoc = await loadingTask.promise;
    try {
        for (const pageNumber of pages) {
            const index = pageNumber - 1;
            if (index < 0 || index >= pdfDoc.getPageCount()) {
                continue;
            }
            const oldPage = pdfDoc.getPage(index);
            const oldRef = oldPage.ref;
            const pdfPage = await renderDoc.getPage(pageNumber);
            const baseViewport = pdfPage.getViewport({ scale: 1 });
            const viewport = pdfPage.getViewport({ scale: REDACT_RENDER_DPI / 72 });

            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.ceil(viewport.width));
            canvas.height = Math.max(1, Math.ceil(viewport.height));
            const context = canvas.getContext('2d', { alpha: false });
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            await pdfPage.render({ intent: 'print', canvasContext: context, viewport }).promise;

            const image = await pdfDoc.embedPng(await canvasToPngBytes(canvas));
            // The new page takes the displayed orientation (rotation baked
            // into the raster), so it carries no /Rotate of its own.
            const newPage = pdfDoc.insertPage(index, [baseViewport.width, baseViewport.height]);
            newPage.drawImage(image, { x: 0, y: 0, width: baseViewport.width, height: baseViewport.height });

            removeFormFieldsOnPage(pdfDoc, oldRef);
            pdfDoc.removePage(index + 1);
        }
    } finally {
        try {
            await renderDoc.destroy();
        } catch (error) {
            // Ignore.
        }
    }

    scrubDocumentMetadata(pdfDoc);
}
